import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import {
  Outlet,
  NavLink,
  useSearchParams,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Container } from 'react-bootstrap';
import {
  BookOpen,
  Search as SearchIcon,
  Headphones,
  Bookmark,
  Mail,
  Sparkles,
  Newspaper,
  ScrollText,
  Quote,
  Network,
  Rss,
} from 'lucide-react';

import AutoComplete from '../component/AutoComplete';
import {
  bookHistory,
  key_bookUrl,
  key_searchPageUrl,
  searchHistory,
} from '../util/constants';
import SignupLoginDialog from '../component/SignupDialog';
import './SearchFilters.css';
const Chatbot = lazy(() => import('../component/Chatbot'));

const SEARCH_TYPES = ['simple', 'vector', 'proximity'];
const defaultSearchType = (x) => (SEARCH_TYPES.includes(x) ? x : 'simple');

const SEARCH_MODES = [
  { value: 'simple', label: 'Simple' },
  { value: 'proximity', label: 'Advanced' },
  { value: 'vector', label: 'Experimental' },
];

const SUGGESTED_SEARCHES = [
  'Annihilation of caste',
  'Constitution',
  'Buddha and his Dhamma',
];

// Same sections, same order as before; only the presentation changes.
const SECTIONS = [
  { to: 'search', label: 'Search', icon: SearchIcon, isSearch: true },
  { to: '.', label: 'Catalogue', icon: BookOpen, end: true },
  { to: 'listen', label: 'Listen', icon: Headphones, isListen: true },
  { to: 'feed', label: 'Feed', icon: Rss },
  { to: 'bookmarks', label: 'Bookmarks', icon: Bookmark },
  { to: 'letters', label: 'Letters', icon: Mail },
  { to: 'vows', label: 'Vows', icon: Sparkles },
  { to: 'bahishkrit_bharat', label: 'Bahishkrit Bharat', icon: Newspaper },
  { to: 'swaraj-critique', label: 'Swaraj Critique', icon: ScrollText },
  { to: 'quotes', label: 'Quotes', icon: Quote },
  { to: 'mindmap', label: 'Mind Map', icon: Network },
];

function SearchModeToggle({ value, onChange }) {
  return (
    <div
      role="radiogroup"
      aria-label="Search mode"
      className="inline-flex rounded-xl bg-indigo-100/70 p-1 gap-0.5"
    >
      {SEARCH_MODES.map((mode) => {
        const active = value === mode.value;
        return (
          <button
            key={mode.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(mode.value)}
            className={`rounded-lg px-3 py-1.5 text-[13px] transition-colors ${
              active
                ? 'bg-white font-semibold text-indigo-800 shadow-sm'
                : 'text-indigo-800/80 hover:text-indigo-900'
            }`}
          >
            {mode.label}
          </button>
        );
      })}
    </div>
  );
}

export default function HomeLayout() {
  const [searchParams] = useSearchParams();
  const [searchType, setSearchType] = useState(
    defaultSearchType(searchParams.get('searchType'))
  );
  const [searchText, setSearchText] = useState(searchParams.get('q') || '');
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === '/';

  const user = useSelector((state) => state.userReducer);

  // AutoComplete keeps the first onSelect it sees in a ref, so read the
  // current mode through a ref as well.
  const searchTypeRef = useRef(searchType);
  searchTypeRef.current = searchType;

  const handleSearch = (text) => {
    const q = text.replace(/^\d+\.\s*/, '');
    const url = `/search?q=${encodeURIComponent(q)}&searchType=${searchTypeRef.current}`;
    searchHistory.set(key_searchPageUrl, url);
    setSearchText(q);
    navigate(url);
  };

  useEffect(() => {
    setSearchText(searchParams.get('q') || '');
    setSearchType(defaultSearchType(searchParams.get('searchType')));
    searchTypeRef.current = defaultSearchType(searchParams.get('searchType'));
    // Re-run the search when the query in the URL changes (e.g. back button).
    if (searchParams.get('q') && searchParams.get('q') !== searchText) {
      handleSearch(searchParams.get('q') || '');
    }
  }, [searchParams]);

  const setBookContextUrl = (e, vol_name, page_num) => {
    e.preventDefault();

    bookHistory.set(key_bookUrl, {
      shortUrl: e.target.href,
      longUrl: `https://assets.studyring.org/assets/books/baws/${vol_name}.pdf#page=${page_num}`,
    });

    navigate(`/books/baws/${vol_name}/pdf/${page_num}`);
  };

  const sectionClassName =
    (section) =>
    ({ isActive }) => {
      const base =
        'flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1.5 text-sm no-underline transition-colors border';
      if (isActive) {
        return `${base} border-indigo-600 bg-indigo-600 font-semibold text-white`;
      }
      if (section.isListen) {
        return `${base} border-sky-300 bg-sky-50 font-semibold text-sky-800 hover:bg-sky-100`;
      }
      return `${base} border-slate-200 bg-white font-medium text-slate-700 hover:border-slate-300 hover:bg-slate-50`;
    };

  const searchBox = (
    <div className="baws-search w-full">
      <AutoComplete
        defaultValue={searchText}
        onSelect={handleSearch}
        onSetBookUrl={setBookContextUrl}
      />
    </div>
  );

  const vectorNote = searchType === 'vector' && (
    <p className="m-0 text-xs text-slate-600">
      Experimental search matches the meaning and context of words, not just the
      exact words. It may be slower than normal.
    </p>
  );

  return (
    <>
      {/* Search band: full hero on Home, compact on every other tab */}
      <section
        className="border-b border-blue-100 bg-blue-50"
        style={{
          paddingTop: window.isNativeApp
            ? 'calc(4rem + env(safe-area-inset-top, 32px))'
            : '4rem',
        }}
      >
        <div
          className={`mx-auto flex max-w-7xl flex-col px-4 ${
            isHome
              ? 'items-center gap-3 pb-6 pt-8 text-center md:pb-8 md:pt-12'
              : 'gap-2 pb-4 pt-4'
          }`}
        >
          {isHome && (
            <>
              <h1 className="m-0 font-serif text-2xl font-bold leading-tight text-indigo-800 md:text-[44px]">
                Dr. Babasaheb Ambedkar Writings and Speeches
              </h1>
              <p className="m-0 text-sm text-slate-600 md:text-[15px]">
                <a
                  href="https://ambedkarinternationalcenter.org"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-slate-600 no-underline hover:text-indigo-700 hover:underline"
                >
                  Ambedkar International Center, USA
                </a>
              </p>
            </>
          )}
          <div
            className={`relative z-[9999] w-full ${isHome ? 'mt-3 max-w-3xl' : ''}`}
          >
            {searchBox}
          </div>
          <div
            className={`flex w-full flex-wrap items-center gap-2 ${
              isHome ? 'max-w-3xl justify-between' : ''
            }`}
          >
            <SearchModeToggle value={searchType} onChange={setSearchType} />
            {isHome && (
              <div className="hidden flex-wrap items-center gap-1.5 text-[13px] text-slate-600 sm:flex">
                <span>Try:</span>
                {SUGGESTED_SEARCHES.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => handleSearch(s)}
                    className="rounded-full border border-blue-200 bg-white px-2.5 py-1 text-blue-800 hover:border-blue-300 hover:bg-blue-50"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>
          {vectorNote}

          {/* App Install Promotional Banner */}
          {!window.isNativeApp &&
            navigator.userAgent.toLowerCase().includes('android') &&
            new Date() < new Date('2026-09-01') && (
              <a
                href="https://play.google.com/store/apps/details?id=in.baws.app"
                target="_blank"
                rel="noopener noreferrer"
                className="self-center rounded-full bg-blue-600 px-5 py-1.5 text-sm font-semibold text-white no-underline shadow"
              >
                Download the Android app
              </a>
            )}
        </div>
      </section>

      {/* Section chips: sticky under the fixed navbar */}
      <div
        className="sticky z-40 border-b border-slate-200 bg-white/95 backdrop-blur"
        style={{
          top: window.isNativeApp
            ? 'calc(4rem + env(safe-area-inset-top, 32px))'
            : '4rem',
        }}
      >
        <nav
          aria-label="Sections"
          className="horizontal-fade mx-auto flex max-w-7xl gap-1.5 overflow-x-auto px-4 py-2.5 [scrollbar-width:none]"
        >
          {SECTIONS.map((section) => {
            const Icon = section.icon;
            const to = section.isSearch
              ? `search?q=${encodeURIComponent(searchText)}&searchType=${searchType}`
              : section.to;
            return (
              <NavLink
                key={section.label}
                to={to}
                end={section.end}
                className={sectionClassName(section)}
              >
                <Icon size={16} aria-hidden="true" />
                <span>{section.label}</span>
              </NavLink>
            );
          })}
        </nav>
      </div>

      {/* Same width as the navbar and search band (max-w-7xl, 16px gutters) */}
      <Container style={{ maxWidth: 1280, paddingLeft: 16, paddingRight: 16 }}>
        <main className="mt-6">
          <Outlet />
        </main>
      </Container>
      {!user.isLoggedIn && <SignupLoginDialog />}
      {/* Render Chatbot on all pages except Book view */}
      {!location.pathname.startsWith('/books/') && (
        <Suspense fallback={null}>
          <Chatbot />
        </Suspense>
      )}
    </>
  );
}
